"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname, useRouter } from "@/i18n/navigation";
import { MapIcon } from "lucide-react";
import { MapSearchResponseDto, searchApi, SearchResultDto } from "@/lib/api/apiwrapper";
import { useTranslations } from "next-intl";
import { HorizontalContainer } from "@/components/layout/containers/HorizontalContainer";
import { VerticalContainer } from "@/components/layout/containers/VerticalContainer";
import { Button } from "@/components/ui/Button";
import { FilterSuggestion, FilterToken, getFilteredSuggestions, getTokenIcon, SearchContext } from "./filterSuggestions";
import { SpendAnalysisIcon } from "@/components/icons/SpendAnalysisIcon";
import { SearchResultsDropdown } from "./SearchResultsDropdown";
import { SearchContextSwitch } from "./SearchContextSwitch";
import { FilterSuggestionDropdown } from "./FilterSuggestionDropdown";

/**
 * Unified search bar for the ExploreNavbar.
 *
 * All search-related state (query, tokens, draft filter) is local.
 * Search context is derived from the current route.
 */
export function ExploreNavbarSearch() {
  const pathname = usePathname();
  const t = useTranslations();
  const router = useRouter();

  // ── Local state ──────────────────────────────────────────
  const [searchContext, setSearchContext] = useState<SearchContext>(pathname?.includes("/map") ? "map" : "spend");

  // Sync search context when the user navigates to a different page
  useEffect(() => {
    setSearchContext(pathname?.includes("/map") ? "map" : "spend");
  }, [pathname]);

  const [searchText, setSearchText] = useState("");
  const [activeTokens, setActiveTokens] = useState<FilterToken[]>([]);
  const [draftFilterKey, setDraftFilterKey] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Search results via API  ─────────────────────────────
  const [isSearching, setIsSearching] = useState(false);
  const [spendResults, setSpendAnalysisResults] = useState<SearchResultDto>({ companies: [], orders: [], invoices: [], matchedCompanyOrders: [], totals: null });
  const [mapResults, setMapResults] = useState<MapSearchResponseDto>({ cities: [], companies: [] });

  const isCompanyPage = pathname?.includes("/company/");
  const isOrdersPage = pathname?.includes("/orders");

  useEffect(() => {
    const searchItems = async () => {
      try {
        if (searchContext === "spend") {
          await spendAnalysisSearch();
        } else {
          await mapSearch();
        }
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setIsSearching(false);
      }
    };

    if (searchText.length >= 3 || activeTokens.length > 0) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
      setSpendAnalysisResults({ companies: [], orders: [], invoices: [], matchedCompanyOrders: [], totals: null });
      setMapResults({ cities: [], companies: [] });
    }

    const debounce = setTimeout(() => {
      if (searchText.length >= 3 || activeTokens.length > 0) {
        searchItems();
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchText, activeTokens, isCompanyPage, isOrdersPage, searchContext]);

  const spendAnalysisSearch = async () => {
    if (searchText.length < 3) {
      setSpendAnalysisResults({ companies: [], orders: [], invoices: [], matchedCompanyOrders: [], totals: null });
      return;
    }
    const filterParams: Record<string, string> = {};
    activeTokens.forEach((f) => {
      filterParams[`${f.key}Filter`] = f.value;
    });

    const result = await searchApi.searchSpendAnalysisGET({
      query: searchText,
      ...filterParams,
    });
    setSpendAnalysisResults(result);
  };

  const mapSearch = async () => {
    if (searchText.length < 3) {
      setMapResults({ cities: [], companies: [] });
      return;
    }
    const filterParams: Record<string, string> = {};
    activeTokens.forEach((f) => {
      filterParams[`${f.key}Filter`] = f.value;
    });

    const result = await searchApi.searchMapGET({
      query: searchText,
      ...filterParams,
    });
    setMapResults(result);
  };

  const commitDraftFilter = (value: string) => {
    if (!draftFilterKey) return;
    const cleanValue = value.trim().replace(/^"|"$/g, "");
    if (!cleanValue) {
      setDraftFilterKey(null);
      setSearchText("");
      return;
    }
    setActiveTokens((prev) => [...prev.filter((t) => t.key !== draftFilterKey), { key: draftFilterKey, value: cleanValue }]);
    setDraftFilterKey(null);
    setSearchText("");
    inputRef.current?.focus();
  };

  const applySuggestion = (suggestion: FilterSuggestion) => {
    setActiveTokens((prev) => [...prev.filter((t) => t.key !== suggestion.key), { key: suggestion.key, value: suggestion.value }]);
    setDraftFilterKey(null);
    setSearchText("");
    inputRef.current?.focus();
  };

  const removeToken = (token: FilterToken) => {
    setActiveTokens((prev) => prev.filter((t) => !(t.key === token.key && t.value === token.value)));
  };

  // ── Input handlers ───────────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!draftFilterKey) {
      const suggestions = getFilteredSuggestions("", null);
      const match = suggestions.find((s) => value.toLowerCase() === s.key.toLowerCase() + ":");
      if (match) {
        setDraftFilterKey(match.key);
        setSearchText("");
        return;
      }
    } else {
      if (value.length > 1 && value.startsWith('"') && value.endsWith('"')) {
        commitDraftFilter(value);
        return;
      }
    }
    setSearchText(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (draftFilterKey) {
      if (e.key === "Backspace" && searchText === "") {
        setSearchText(draftFilterKey + ":");
        setDraftFilterKey(null);
        e.preventDefault();
      } else if (e.key === " " || e.key === "Enter") {
        if (searchText.startsWith('"') && !searchText.endsWith('"')) {
          // Inside an open quote — allow space/enter
        } else if (searchText.trim().length > 0) {
          commitDraftFilter(searchText);
          e.preventDefault();
        }
      }
    } else if (e.key === "Enter" && (searchText.length >= 3 || activeTokens.length > 0)) {
      // Redirect to search results page
      const params = new URLSearchParams();
      params.set("q", searchText);
      activeTokens.forEach(t => params.append(t.key, t.value));

      setIsInputFocused(false);
      router.push({
        pathname: "/search",
        query: Object.fromEntries(params.entries())
      });
    }
  };

  const handleContextChange = (newContext: SearchContext) => {
    setSearchContext(newContext);
    setActiveTokens([]);
    setSearchText("");
    setDraftFilterKey(null);
    inputRef.current?.focus();
  };

  const filteredSuggestions = getFilteredSuggestions(searchText, draftFilterKey);

  return (
    <div className="relative mx-auto w-full max-w-3xl flex-1">
      {/* Search Input + Active Tokens */}
      <div className="relative flex items-center rounded-md border border-input bg-background focus-within:border-ring focus-within:ring-1 focus-within:ring-ring">
        {/* Context Icon */}
        <div className="pointer-events-none absolute left-0 flex w-8 items-center justify-center pl-3 text-muted-foreground">
          <AnimatePresence
            mode="popLayout"
            initial={false}
          >
            {searchContext === "spend" ? (
              <motion.div
                key="spend"
                initial={{ opacity: 0, scale: 0.5, rotate: -45 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.5, rotate: 45 }}
                transition={{ duration: 0.2 }}
              >
                <SpendAnalysisIcon className="h-4 w-4" />
              </motion.div>
            ) : (
              <motion.div
                key="map"
                initial={{ opacity: 0, scale: 0.5, rotate: -45 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.5, rotate: 45 }}
                transition={{ duration: 0.2 }}
              >
                <MapIcon className="h-4 w-4" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {searchContext === "map" && draftFilterKey && (
          <HorizontalContainer className="pl-10">
            <div className="inline-flex items-center rounded border border-primary/20 bg-primary/10 px-2 py-0.5">
              <span className="text-xs font-semibold text-primary">{draftFilterKey}:</span>
            </div>
          </HorizontalContainer>
        )}

        <input
          ref={inputRef}
          type="search"
          placeholder={searchContext === "map" && draftFilterKey ? "value..." : t("search.search")}
          className={`block flex-1 ${searchContext === "map" && draftFilterKey ? "pl-2" : "pl-10"} min-w-[100px] border-0 bg-transparent py-2 pr-3 focus:outline-none focus:ring-0 sm:text-sm`}
          value={searchText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsInputFocused(true)}
          onBlur={(e) => {
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (!relatedTarget?.closest(".search-dropdown")) {
              setIsInputFocused(false);
            }
          }}
        />

        {searchContext === "map" && activeTokens.length > 0 && (
          <HorizontalContainer className="flex-wrap pr-2 gap-1">
            {activeTokens.map((token) => (
              <div
                key={`${token.key}:${token.value}`}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5"
              >
                <span className="text-muted-foreground">{getTokenIcon(token)}</span>
                <span className="text-xs font-medium text-foreground">
                  {token.key}:{token.value}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => removeToken(token)}
                  className="ml-1 text-muted-foreground hover:text-foreground p-0 h-6 w-2.5"
                >
                  ×
                </Button>
              </div>
            ))}
          </HorizontalContainer>
        )}
      </div>

      {/* Dropdown */}
      {isInputFocused && (
        <div className="search-dropdown absolute z-20 mt-2 w-full overflow-hidden rounded-md border border-border bg-background shadow-lg">
          {searchContext === "map" && (
            <FilterSuggestionDropdown
              suggestions={filteredSuggestions}
              onApply={(suggestion) => {
                applySuggestion(suggestion);
                setIsInputFocused(true);
              }}
            />
          )}
          <VerticalContainer className="min-h-[250px]">
            <SearchResultsDropdown
              context={searchContext}
              searchText={searchText}
              hasTokens={activeTokens.length > 0}
              spendResults={spendResults}
              mapResults={mapResults}
              onResultClick={() => setIsInputFocused(false)}
              isSearching={isSearching}
            />
          </VerticalContainer>

          <SearchContextSwitch
            searchContext={searchContext}
            onContextChange={handleContextChange}
          />
        </div>
      )}
    </div>
  );
}
